// shippingTool page strings. Keys are read as $t('shippingTool.<key>').
// rulesHtml is the "Quy tắc" tab, rendered with v-html; {readSetting}, {blankGroup} and
// {cdLevel} are filled in live (HTML-escaped) and literal {n} in column names is kept.
// Field labels in the view's FIELDS stay the Excel column names (Khung gio, Nhom Packages, ...)
// in both languages, because Export / Import use exactly those headers.
export default {
  en: {
    title: 'Shipping Tool',
    subtitle: 'Assign shippers for Mon–Fri from the Fitfood Planning file.',
    tabs: {
      process: 'Process',
      shiptimeslot: 'Shiptimeslot',
      packages: 'Packages',
      wardshiprule: 'Wardshiprule',
      shipcodinh: 'Fixed ship',
      setting: 'Setting',
      rules: 'Rules',
    },
    process: {
      configTitle: 'Configuration',
      noConfig: 'No configuration yet. Go to the Setting tab and add a record named "read".',
      title: 'Process & assign shippers',
      hint: 'Pick the Fitfood Planning file (.xlsx / .xlsb). The result file has 3 sheets: <strong>B1</strong>, <strong>Final</strong> and <strong>UnassignedReasons</strong>.',
      run: 'Process & assign shippers',
      doneCount: '{assigned}/{total} rows have a shipper — result file downloaded.',
      done: 'Result file downloaded.',
      failed: 'Unknown error while processing the file.',
    },
    notSet: '(not set)',
    newButton: '+ New',
    export: '⬇ Export',
    import: '⬆ Import',
    search: 'Search...',
    showInactive: 'Show inactive',
    count: '{shown} / {total} records',
    close: 'Close',
    importTitle: 'Import {model}:',
    created: 'Created {n}',
    updated: 'Updated {n}',
    skipped: 'Skipped {n}',
    failed: 'Failed {n}',
    unknownPackages: 'Unknown package {n}',
    errorsToggle: '{n} errors',
    warningsToggle: '{n} warnings (unchanged)',
    active: 'Active',
    inactive: 'Inactive',
    edit: 'Edit',
    deactivate: 'Deactivate',
    modalNew: 'New {label}',
    modalEdit: 'Edit {label}',
    status: 'Status',
    statusActive: 'Active',
    cancel: 'Cancel',
    save: 'Save',
    saving: 'Saving…',
    saveFailed: 'Save failed.',
    headers: {
      id: 'ID',
      status: 'Status',
      actions: 'Actions',
    },
    hints: {
      ruleOrder: '1 = street, 5 = ward, 99 = fallback',
      priority: 'Only applies to rule_order=1. Smaller numbers run first; empty = sorted after every number.',
      packages: 'Package code (FIT1, CONO, ...). Empty = FITFOOD.',
      setting: 'The setting name is the lookup key — it cannot be changed. Only edit Value.',
    },
    alerts: {
      loadFailed: 'Could not load the data.',
      confirmDeactivate: 'Set "{label}" to Inactive?',
      deactivateFailed: 'Could not update the status.',
      exportFailed: 'Export failed.',
      unknownError: 'Unknown error',
      unknownErrorWith: 'Unknown error: {detail}',
    },
    rulesHtml: `
<p class="ffc-muted">
  Every rule below <strong>only uses records whose status = Active</strong>.
  Inactive records are ignored completely.
</p>

<h2 class="st-h2">Normalising text before matching</h2>
<p>
  Every text comparison (ward, street, package group, time-slot group, address) is
  <strong>not</strong> made directly: <strong>both sides</strong> first go through the
  normalisation below. That is why <code>"Quận Bình Thạnh"</code> and
  <code>"quan binh thanh"</code> count as the same.
</p>
<ol>
  <li>Remove the Excel artefact <code>_x000d_</code> (replaced by a space).</li>
  <li><code>đ</code> / <code>Đ</code> → <code>d</code>.
    <span class="ffc-muted st-small">This step must come <em>first</em>, because <code>đ</code> cannot be split into a letter plus a mark like the other letters.</span></li>
  <li>Decompose (NFD), then <strong>drop every tone mark and diacritic</strong>:
    <code>ế</code> → <code>e</code>, <code>ạ</code> → <code>a</code>, <code>ơ</code> → <code>o</code>.</li>
  <li>Convert everything to <strong>lower case</strong>.</li>
  <li>Collapse runs of spaces into one and trim leading/trailing spaces.</li>
</ol>
<p class="st-small">
  Example: <code>"Phường Bến Nghé,  Quận 01 "</code> → <code>"phuong ben nghe, quan 01"</code>.
  <br />
  <strong>Note:</strong> punctuation (<code>,</code> <code>/</code> <code>.</code>) is <em>not</em> removed — only Vietnamese diacritics are.
</p>

<h3 class="st-h3">Matching package codes (one step stricter)</h3>
<p>
  Package codes additionally have <strong>every character that is not a letter or digit removed</strong> after
  normalising, because real data is written very loosely. So
  <code>MEAT-S</code>, <code>MEAT - S</code>, <code>MeaT S</code>, <code>meat-s</code>
  all become <code>meats</code> and match the same package.
  Lookup order: an <strong>exact</strong> match first; only if that fails is this looser match used.
</p>

<h3 class="st-h3">Address similarity (used in Tier 1)</h3>
<p>The score is the <strong>share of shared words</strong>, not a character-by-character comparison:</p>
<ol>
  <li>Normalise both addresses with the steps above.</li>
  <li>Split them into <strong>words</strong> (tokens), separated by any character that is not a letter or digit.</li>
  <li>Count the words of the order address that also appear in the record's address → <code>shared</code>.</li>
  <li><code>total = words in A + words in B − shared</code>.</li>
  <li>Score <code>= round(shared ÷ total × 100)</code>, at most 100.</li>
</ol>
<table class="ffc-table st-doc-table">
  <thead><tr><th>Order address (A)</th><th>Record's Dia chi (B)</th><th>Score</th></tr></thead>
  <tbody>
    <tr><td>115 Nguyễn Huệ, Bến Nghé, Quận 01<br /><span class="ffc-muted st-small">7 words</span></td>
        <td>115 Nguyễn Huệ, Phường Bến Nghé, Quận 01<br /><span class="ffc-muted st-small">8 words</span></td>
        <td>shared 7 / total 8 → <strong>88</strong></td></tr>
    <tr><td>115 Nguyễn Huệ<br /><span class="ffc-muted st-small">3 words</span></td>
        <td>Toà nhà Sunwah, 115 Nguyễn Huệ, Phường Bến Nghé, Quận 01, TP HCM<br /><span class="ffc-muted st-small">14 words</span></td>
        <td>shared 3 / total 14 → <strong>21</strong></td></tr>
  </tbody>
</table>
<p class="st-small">
  Consequences to remember: <strong>word order does not matter</strong>;
  <strong>a typo inside a word loses that whole word</strong> (<code>Nguyen</code> ≠ <code>Nguyeen</code>);
  and <strong>the wordier the record's address, the lower the score</strong>, even when it is the right place —
  this is the most common reason Tier 1 does not match.
</p>

<h2 class="st-h2">Step 1 — Read &amp; normalise the Planning file</h2>
<ol>
  <li>Read the <code>read</code> setting to know which column is where.
    <br /><span class="ffc-muted st-small">Current value: <code>{readSetting}</code></span></li>
  <li>The file must have a sheet named <code>Shipping</code>. Data is read from <strong>row 25</strong> down.</li>
  <li>Completely empty rows are dropped, and so are rows with <code>id = 0</code> (the "do not process" marker).</li>
  <li><strong>The time slot for all 5 days</strong> comes from the order's own <code>Shiptimeslot</code> column
    in the Planning file.
    <br /><strong class="ffc-danger-text">There is no default time slot.</strong>
    If that column is empty, that day's time slot is <strong>empty</strong>, and that day
    <strong>matches no tier</strong> (all 4 tiers require a time-slot group).
    <br /><span class="ffc-muted st-small">The Shiptimeslot table is only used to look up <em>time slot → group</em> (Morning / Evening);
    it is not used as a default, and the order of its rows has no effect.</span>
  </li>
  <li><code>address_T{n}</code>: empty → use <code>address_main</code>;
    contains <code>:</code> → split into "time slot : address" (split only at the first <code>:</code>),
    and the time slot written here <strong>overrides</strong> the time slot for that day only;
    prefix <code>Giao tối</code> → time slot <code>1900/2200</code>.</li>
  <li><code>processed</code>: <code>yes</code> → that row <strong>is not assigned a shipper</strong>; any other value → <code>no</code>.</li>
  <li><code>nhom_shiptimeslot_T{n}</code>: look the time slot up in the Shiptimeslot table — exact match first,
    otherwise match again after removing diacritics and collapsing spaces.</li>
  <li><code>nhom_packages</code>: look the package code up in the Packages table.
    <ul>
      <li>Empty → <code>{blankGroup}</code> (setting <code>blank_nhom_packages</code>).</li>
      <li>Misspelt (<code>Fit3</code>, <code>MEAT - S</code>, <code>V EG</code>...) → still matches thanks to comparing after removing diacritics/spaces/dashes.</li>
      <li>Not found → left empty → <strong>that row matches no tier</strong> and is written to UnassignedReasons.</li>
    </ul>
  </li>
</ol>

<h2 class="st-h2">Step 2 — Assign shippers</h2>
<p>Each day Mon..Fri is evaluated <strong>independently</strong>. Rows with <code>processed = yes</code> are skipped.</p>

<div class="ffc-alert ffc-alert-info st-callout">
  <strong>The most important principle: once a higher tier matches, lower tiers are NOT evaluated.</strong>
  <br />
  Tiers are tried in order 1 → 2 → 3 → 4. As soon as a tier returns a <strong>non-empty Shipper</strong>,
  it stops and takes that result; the remaining tiers <strong>do not run</strong>.
  Only when there is still no Shipper does it move on to the next tier.
  <br /><br />
  So Tier 1 <strong>always beats</strong> Tier 2, Tier 2 always beats Tier 3... even when the lower tier
  looks "more correct". To make a rule win, put it in a higher tier — not by editing a rule in a lower tier.
  <br /><br />
  <strong>Exception:</strong> if a rule meets every condition but its <code>Shipper</code> cell is empty,
  it still counts as <em>no shipper yet</em> → it moves on to the next tier, and the reason is written to UnassignedReasons.
</div>

<h3 class="st-h3">Tier 1 — Fixed ship (in detail)</h3>
<p>Go through the <strong>whole</strong> Fixed ship table (Active). A record is a candidate when it meets <strong>all 4</strong> conditions:</p>
<ol>
  <li><strong>Same phone</strong> — both sides first have <strong>every non-digit character removed</strong>, then are compared.
    So <code>0912-345-678</code>, <code>0912345678</code>, <code>(0912) 345 678</code> are the same.
    The comparison is <strong>strict equality</strong>, not "contains".</li>
  <li><strong>Same time-slot group</strong> — take the record's <code>Khung gio</code>,
    look it up in the Shiptimeslot table to get its <code>Nhom khung gio</code>, then compare it with
    the <code>nhom_shiptimeslot_T{n}</code> of the day being evaluated.
    <br /><span class="ffc-danger-text st-small">If the record's <code>Khung gio</code> cannot be looked up (typo, stray characters),
    the group is empty → that record <strong>never matches</strong>.</span></li>
  <li><strong>Same package group</strong> — take the record's <code>Packages</code>, look its group up in the Packages table
    (empty → <code>{blankGroup}</code>), then compare it with the order row's <code>nhom_packages</code>.
    <br /><span class="ffc-danger-text st-small">A wrong package code that cannot be looked up → group is empty → the record <strong>never matches</strong>.</span></li>
  <li><strong>Address similarity ≥ <code>{cdLevel}</code></strong> — compare
    the <code>address_T{n}</code> of the day being evaluated with the record's <code>Dia chi</code>
    (<em>not</em> <code>address_main</code>).</li>
</ol>
<p>
  Among the candidates, the record with the <strong>highest score</strong> wins; on a tie, the <strong>smaller id</strong>.
  Writes <code>rulematch = co dinh</code>, <code>notematch =</code> score %.
</p>
<p class="ffc-muted st-small">
  The <code>hascodinh_T{n}</code> column scores by <strong>phone only</strong> — it ignores the other 3 conditions and the threshold.
  A high score without <code>co dinh</code> ⇒ blocked by the time-slot group or the package group, not by the address.
</p>
<p class="ffc-danger-text st-small">
  Warning: if the order row has <strong>no phone</strong> and the record has <strong>no phone</strong> either,
  condition 1 still counts as equal (empty = empty). The result then depends on the address alone — easy to mismatch.
  Always fill in the phone on Fixed ship records.
</p>

<h3 class="st-h3">Tier 2 — Wardshiprule <code>rule_order = 1</code> (in detail)</h3>
<p>Only rules with <code>rule_order = 1</code> are considered. A rule matches when it meets <strong>all 4</strong>:</p>
<ol>
  <li><strong>Same ward</strong> — the row's <code>ward_T{n}</code> compared with the rule's <code>Ward</code>,
    after normalising. <strong>Strict equality</strong>, not "contains".
    <br /><span class="ffc-danger-text st-small">If <code>ward_T{n}</code> is empty, both Tier 2 and Tier 3 miss —
    only Tier 4 can catch it. The ward is <strong>not</strong> inferred from <code>address_main</code>.</span></li>
  <li><strong>Same package group</strong> — the row's <code>nhom_packages</code> compared with the rule's <code>Nhom Packages</code> (normalised).</li>
  <li><strong>Same time-slot group</strong> — <code>nhom_shiptimeslot_T{n}</code> compared with the rule's <code>Nhom Shiptimeslot</code> (normalised).</li>
  <li><strong>Street contained in the address</strong> — normalise both the rule's <code>Street</code> and <code>address_T{n}</code>,
    then check whether the Street is a <strong>substring</strong> of the address.
    <br />This is a <strong>raw substring</strong> check, <strong>not on word boundaries</strong>: the Street
    <code>"An Dương"</code> also matches the address <code>"An Dương Vương"</code>. Be careful with short Street names.
    <br /><span class="ffc-muted st-small">Empty Street → the rule is skipped (does not count as a match).</span></li>
</ol>
<p>
  <strong>Evaluation order decides the winner</strong> when several rules match — because the <strong>first</strong> matching rule is taken:
  smaller <code>Priority</code> runs first → <strong>empty sorts after every number</strong> → same level goes by Street name (a→z).
  Writes <code>rulematch = 1</code>, <code>notematch =</code> the matched Street name.
</p>

<h3 class="st-h3">Tier 3 — Wardshiprule <code>rule_order = 5</code> (in detail)</h3>
<p>
  This is the <strong>per-ward rule</strong>: the whole ward shares one shipper, whatever the street.
  It only runs when neither Tier 1 nor Tier 2 produced a shipper. A rule matches when it meets <strong>all 3</strong>:
</p>
<ol>
  <li><strong>Same ward</strong> — <code>ward_T{n}</code> compared with the rule's <code>Ward</code>, after normalising.
    Strict equality, not "contains".
    <br /><span class="ffc-danger-text st-small"><code>ward_T{n}</code> empty → misses Tier 3 too, same as Tier 2.</span></li>
  <li><strong>Same package group</strong> — the row's <code>nhom_packages</code> compared with the rule's <code>Nhom Packages</code>.</li>
  <li><strong>Same time-slot group</strong> — <code>nhom_shiptimeslot_T{n}</code> compared with the rule's <code>Nhom Shiptimeslot</code>.</li>
</ol>
<p>
  It differs from Tier 2 in exactly one way: <strong>the <code>Street</code> column is not checked</strong>. In other words, Tier 2 is
  "who delivers this street", Tier 3 is "who delivers the rest of the ward".
</p>
<p>
  <strong>Evaluation order:</strong> the <strong>first matching rule by <code>id</code></strong> (creation order / row
  order at import). <code>Priority</code> does <strong>not</strong> apply at this tier.
  <br /><span class="ffc-danger-text st-small">So if two <code>rule_order = 5</code> rules overlap
  (ward + package group + time-slot group), the one with the smaller <code>id</code> always wins and the other
  <strong>never runs</strong> — the real data has a few such pairs. To change the shipper, edit
  the winning rule; do not add a new one.</span>
  Writes <code>rulematch = 5</code>, <code>notematch</code> left empty.
</p>

<h3 class="st-h3">Tier 4 — Wardshiprule <code>rule_order = 99</code> (in detail)</h3>
<p>
  This is the <strong>safety net</strong>: it runs when none of the 3 tiers above produced a shipper, so the order still gets delivered.
  A rule matches when it meets <strong>both</strong>:
</p>
<ol>
  <li><strong>Same package group</strong> — compared with the row's <code>nhom_packages</code>.</li>
  <li><strong>Same time-slot group</strong> — compared with the day's <code>nhom_shiptimeslot_T{n}</code>.</li>
</ol>
<p>
  <strong>Ward and street are not checked.</strong> Where the address is makes no difference — only the
  package group and time-slot group must match. The <code>Ward</code> column of these rules usually says
  <code>Còn lại</code> and <code>Street</code> says <code>ALL</code>, but that is only <em>a note for readers</em>;
  the code does not read those two columns at this tier at all.
</p>
<p>
  <strong>Evaluation order:</strong> the <strong>first matching rule by <code>id</code></strong>, same as Tier 3.
  In practice each (package group × time-slot group) pair needs only <strong>one</strong> rule; later duplicates
  never run. Writes <code>rulematch = 99</code>, <code>notematch</code> left empty.
</p>
<p class="ffc-danger-text">
  You need all <strong>4 pairs</strong> so no order is ever left out:
  FITFOOD/Morning, FITFOOD/Evening, CONO/Morning, CONO/Evening.
  If a pair is missing, every order in that pair falls through to "no shipper" and only shows up in the
  UnassignedReasons sheet — this is the easiest thing to miss when adding a new package group or time slot.
</p>

<h3 class="st-h3">Summary of the 4 tiers</h3>
<table class="ffc-table st-doc-table">
  <thead>
    <tr><th>Tier</th><th>Source</th><th>Match conditions</th><th>Which record wins</th><th><code>rulematch</code></th></tr>
  </thead>
  <tbody>
    <tr>
      <td><strong>1</strong></td><td>Fixed ship</td>
      <td>phone (digits only) + time-slot group + package group
        + address similarity ≥ <code>{cdLevel}</code></td>
      <td><strong>highest</strong> similarity; on a tie, the smaller id</td>
      <td><code>co dinh</code></td>
    </tr>
    <tr>
      <td><strong>2</strong></td><td>Wardshiprule <code>rule_order = 1</code></td>
      <td>ward + package group + time-slot group + <code>Street</code> contained in the address</td>
      <td>smaller <code>Priority</code> runs first; empty sorts after every number; same level goes by Street name</td>
      <td><code>1</code></td>
    </tr>
    <tr>
      <td><strong>3</strong></td><td>Wardshiprule <code>rule_order = 5</code></td>
      <td>ward + package group + time-slot group (street not checked)</td>
      <td>first record by id — <code>Priority</code> does not apply</td>
      <td><code>5</code></td>
    </tr>
    <tr>
      <td><strong>4</strong></td><td>Wardshiprule <code>rule_order = 99</code></td>
      <td>package group + time-slot group (ward and street not checked)</td>
      <td>first record by id — <code>Priority</code> does not apply</td>
      <td><code>99</code></td>
    </tr>
  </tbody>
</table>
<p>
  Common to all 4 tiers: a rule that meets every condition but has <strong>an empty Shipper cell</strong> still counts as
  no match — it moves on to the next tier and the reason is written to the UnassignedReasons sheet.
</p>

<h2 class="st-h2">Output — 47 columns, 3 sheets</h2>
<ul>
  <li><strong>Final</strong> — the shipper assignment (first sheet).</li>
  <li><strong>UnassignedReasons</strong> — every day cell with no shipper, with the reason.</li>
  <li><strong>B1</strong> — the Step 1 result, with the assignment columns still empty (for cross-checking).</li>
</ul>
<ul>
  <li><code>shipper_T{n}</code> — the assigned shipper.</li>
  <li><code>rulematch_T{n}</code> — which tier decided: <code>co dinh</code> / <code>1</code> / <code>5</code> / <code>99</code>.</li>
  <li><code>notematch_T{n}</code> — the evidence: tier 1 writes the score %, tier 2 writes the matched Street name.</li>
  <li><code>hascodinh_T{n}</code> — <strong>a 0–100 number column</strong>: address similarity with any Fixed ship record
    <strong>with the same phone</strong>, even when tier 1 did not run. Empty = no record has that phone.
    Use it to see why tier 1 did not match: a high score without <code>co dinh</code> means it was blocked by the
    time-slot group or the package group; a score of 70–79 means it only missed the <code>ship_co_dinh_level</code> threshold.</li>
</ul>

<h2 class="st-h2">Excel import guide</h2>
<p>
  The safest way to edit data in bulk: <strong>Export → edit in Excel → Import that same file back</strong>.
  The <code>id</code> column decides whether each row is a new record or an update, so
  <strong class="ffc-danger-text">do not edit or delete the <code>id</code> column</strong> of existing rows.
</p>

<h3 class="st-h3">Steps</h3>
<ol>
  <li>Go to the tab you want to edit and click <strong>⬇ Export</strong>. The downloaded file has <code>id</code> as the first column,
    <code>status</code> as the last, and contains <strong>both Active and Inactive</strong> records.</li>
  <li>Edit in Excel. To add a row, add it at the end and <strong>leave its <code>id</code> cell empty</strong>.</li>
  <li>Save as <code>.xlsx</code>, click <strong>⬆ Import</strong> and pick the file.</li>
  <li>Read the result panel shown on screen before doing anything else.</li>
</ol>

<h3 class="st-h3">Import handles each row by its <code>id</code> column</h3>
<table class="ffc-table st-doc-table">
  <thead><tr><th><code>id</code> cell</th><th>Result</th><th>Shown in the report</th></tr></thead>
  <tbody>
    <tr><td>empty</td><td><strong>A new record is added</strong></td><td><span class="ffc-pill ffc-pill-ok">Created</span></td></tr>
    <tr><td>set, exists, <strong>changed</strong></td><td>Updates <strong>only the fields that changed</strong></td><td><span class="ffc-pill st-pill-info">Updated</span></td></tr>
    <tr><td>set, exists, <strong>identical</strong></td><td>Nothing is written</td><td><span class="ffc-pill ffc-pill-warn">Skipped</span> + warning</td></tr>
    <tr><td>set but <strong>does not exist</strong></td><td>Skips <em>that row only</em>; the other rows still run</td><td><span class="ffc-pill ffc-pill-danger">Failed</span> + error</td></tr>
  </tbody>
</table>

<h3 class="st-h3">Common mistakes</h3>
<ul>
  <li><strong>Import does not delete old data.</strong> A row removed from the Excel file stays in the system —
    to drop a record set <code>status = Inactive</code>; do not delete the row.</li>
  <li>The <code>status</code> column: <code>Active</code> / <code>Inactive</code>.
    Empty on an update = <strong>keep</strong> the current status.</li>
  <li><strong>Column names are case-insensitive</strong> and ignore spaces and underscores, so
    <code>packages</code> and <code>Packages</code> both work. A column missing from the file leaves that field
    <strong>unchanged</strong>; it is not cleared.</li>
  <li>Only the <strong>first sheet</strong> of the file is read.</li>
  <li><strong class="ffc-danger-text">Importing the original <code>ShipManagement_Database.xlsx</code> twice duplicates the data</strong>,
    because that file has no <code>id</code> column so every row counts as new. Import that file only
    <strong>once</strong> at setup; after that always use an Export file.</li>
  <li><strong>Fixed ship</strong> tab only: a <code>Packages</code> code that cannot be looked up shows the
    <span class="ffc-pill ffc-pill-warn">Unknown package</span> tag. The record is still saved but
    <strong>matches no order</strong> until the code is fixed.</li>
  <li><strong>Setting</strong> tab only: values can only be edited — no adding, no deactivating,
    not even via Import (rows with an empty <code>id</code> or <code>status = Inactive</code> are skipped with a warning).</li>
</ul>

<h3 class="st-h3">Deleting data</h3>
<p>
  Nothing is ever really deleted. The <strong>Deactivate</strong> button only switches <code>status</code> to
  <code>Inactive</code>; the record stays in the table and is still exported, but is ignored completely
  when assigning shippers. Turn on <strong>Show inactive</strong> to see it again.
</p>
`,
  },
  vi: {
    title: 'Shipping Tool',
    subtitle: 'Chia shipper T2–T6 từ file Fitfood Planning.',
    tabs: {
      process: 'Xử lý',
      shiptimeslot: 'Shiptimeslot',
      packages: 'Packages',
      wardshiprule: 'Wardshiprule',
      shipcodinh: 'Ship cố định',
      setting: 'Setting',
      rules: 'Quy tắc',
    },
    process: {
      configTitle: 'Cấu hình',
      noConfig: 'Chưa có cấu hình. Vào tab Setting và thêm bản ghi tên "read".',
      title: 'Xử lý & Chia ship',
      hint: 'Chọn file Fitfood Planning (.xlsx / .xlsb). File kết quả gồm 3 sheet: <strong>B1</strong>, <strong>Final</strong> và <strong>UnassignedReasons</strong>.',
      run: 'Xử lý & Chia ship',
      doneCount: '{assigned}/{total} dòng có shipper — đã tải file kết quả.',
      done: 'Đã tải file kết quả.',
      failed: 'Lỗi không xác định khi xử lý file.',
    },
    notSet: '(chưa có)',
    newButton: '+ Thêm',
    export: '⬇ Export',
    import: '⬆ Import',
    search: 'Tìm kiếm...',
    showInactive: 'Hiện mục ngưng dùng',
    count: '{shown} / {total} bản ghi',
    close: 'Đóng',
    importTitle: 'Import {model}:',
    created: 'Thêm mới {n}',
    updated: 'Cập nhật {n}',
    skipped: 'Bỏ qua {n}',
    failed: 'Lỗi {n}',
    unknownPackages: 'Package lạ {n}',
    errorsToggle: '{n} lỗi',
    warningsToggle: '{n} cảnh báo (không thay đổi)',
    active: 'Đang dùng',
    inactive: 'Ngưng dùng',
    edit: 'Sửa',
    deactivate: 'Ngưng dùng',
    modalNew: 'Thêm {label}',
    modalEdit: 'Sửa {label}',
    status: 'Trạng thái',
    statusActive: 'Đang dùng',
    cancel: 'Huỷ',
    save: 'Lưu',
    saving: 'Đang lưu…',
    saveFailed: 'Lưu không thành công.',
    headers: {
      id: 'ID',
      status: 'Trạng thái',
      actions: 'Thao tác',
    },
    hints: {
      ruleOrder: '1 = street, 5 = ward, 99 = dự phòng',
      priority: 'Chỉ áp dụng cho rule_order=1. Số nhỏ chạy trước; để trống = xếp sau tất cả các số.',
      packages: 'Mã package (FIT1, CONO, ...). Để trống = FITFOOD.',
      setting: 'Tên setting là khoá tra cứu — không sửa được. Chỉ sửa Value.',
    },
    alerts: {
      loadFailed: 'Không tải được dữ liệu.',
      confirmDeactivate: 'Chuyển "{label}" sang Inactive?',
      deactivateFailed: 'Không thể cập nhật trạng thái.',
      exportFailed: 'Export thất bại.',
      unknownError: 'Lỗi không xác định',
      unknownErrorWith: 'Lỗi không xác định: {detail}',
    },
    rulesHtml: `
<p class="ffc-muted">
  Toàn bộ quy tắc dưới đây <strong>chỉ dùng bản ghi có status = Active</strong>.
  Bản ghi Inactive bị bỏ qua hoàn toàn.
</p>

<h2 class="st-h2">Chuẩn hoá chuỗi trước khi so khớp</h2>
<p>
  Mọi phép so khớp chữ (ward, street, nhóm package, nhóm khung giờ, địa chỉ) đều
  <strong>không</strong> so trực tiếp, mà chạy qua hàm chuẩn hoá dưới đây trên
  <strong>cả hai vế</strong>. Nhờ vậy <code>"Quận Bình Thạnh"</code> và
  <code>"quan binh thanh"</code> được coi là giống nhau.
</p>
<ol>
  <li>Xoá rác Excel <code>_x000d_</code> (thay bằng khoảng trắng).</li>
  <li><code>đ</code> / <code>Đ</code> → <code>d</code>.
    <span class="ffc-muted st-small">Phải làm bước này <em>trước</em>, vì <code>đ</code> không tách được dấu như các chữ khác.</span></li>
  <li>Tách dấu (NFD) rồi <strong>bỏ toàn bộ dấu thanh và dấu mũ</strong>:
    <code>ế</code> → <code>e</code>, <code>ạ</code> → <code>a</code>, <code>ơ</code> → <code>o</code>.</li>
  <li>Chuyển hết về <strong>chữ thường</strong>.</li>
  <li>Gộp nhiều khoảng trắng liên tiếp thành một, cắt khoảng trắng đầu/cuối.</li>
</ol>
<p class="st-small">
  Ví dụ: <code>"Phường Bến Nghé,  Quận 01 "</code> → <code>"phuong ben nghe, quan 01"</code>.
  <br />
  <strong>Lưu ý:</strong> dấu câu (<code>,</code> <code>/</code> <code>.</code>) <em>không</em> bị xoá — chỉ dấu tiếng Việt mới bị bỏ.
</p>

<h3 class="st-h3">So khớp mã package (chặt hơn một bước)</h3>
<p>
  Riêng mã package còn bị <strong>bỏ luôn mọi ký tự không phải chữ/số</strong> sau khi chuẩn hoá,
  vì dữ liệu thực tế viết rất tuỳ tiện. Nhờ vậy
  <code>MEAT-S</code>, <code>MEAT - S</code>, <code>MeaT S</code>, <code>meat-s</code>
  đều quy về <code>meats</code> và khớp cùng một package.
  Cách tra: khớp <strong>chính xác</strong> trước, không được mới khớp kiểu này.
</p>

<h3 class="st-h3">Độ giống địa chỉ (dùng ở Mức 1)</h3>
<p>Điểm giống là <strong>tỉ lệ trùng từ</strong>, không phải so từng ký tự:</p>
<ol>
  <li>Chuẩn hoá cả hai địa chỉ theo các bước trên.</li>
  <li>Cắt thành các <strong>từ</strong> (token), ngăn cách bởi mọi ký tự không phải chữ/số.</li>
  <li>Đếm số từ của địa chỉ đơn hàng có xuất hiện trong địa chỉ bản ghi → <code>trùng</code>.</li>
  <li><code>tổng = số từ A + số từ B − trùng</code>.</li>
  <li>Điểm <code>= làm tròn(trùng ÷ tổng × 100)</code>, tối đa 100.</li>
</ol>
<table class="ffc-table st-doc-table">
  <thead><tr><th>Địa chỉ đơn (A)</th><th>Dia chi của bản ghi (B)</th><th>Điểm</th></tr></thead>
  <tbody>
    <tr><td>115 Nguyễn Huệ, Bến Nghé, Quận 01<br /><span class="ffc-muted st-small">7 từ</span></td>
        <td>115 Nguyễn Huệ, Phường Bến Nghé, Quận 01<br /><span class="ffc-muted st-small">8 từ</span></td>
        <td>trùng 7 / tổng 8 → <strong>88</strong></td></tr>
    <tr><td>115 Nguyễn Huệ<br /><span class="ffc-muted st-small">3 từ</span></td>
        <td>Toà nhà Sunwah, 115 Nguyễn Huệ, Phường Bến Nghé, Quận 01, TP HCM<br /><span class="ffc-muted st-small">14 từ</span></td>
        <td>trùng 3 / tổng 14 → <strong>21</strong></td></tr>
  </tbody>
</table>
<p class="st-small">
  Hệ quả cần nhớ: <strong>thứ tự từ không quan trọng</strong>;
  <strong>sai chính tả trong một từ là mất luôn từ đó</strong> (<code>Nguyen</code> ≠ <code>Nguyeen</code>);
  và <strong>bản ghi ghi địa chỉ càng dài dòng thì điểm càng thấp</strong>, kể cả khi đúng chỗ đó —
  đây là lý do phổ biến nhất khiến Mức 1 không khớp.
</p>

<h2 class="st-h2">Bước 1 — Đọc &amp; chuẩn hoá file Planning</h2>
<ol>
  <li>Đọc cấu hình <code>read</code> trong Setting để biết cột nào nằm ở đâu.
    <br /><span class="ffc-muted st-small">Hiện tại: <code>{readSetting}</code></span></li>
  <li>File phải có sheet tên <code>Shipping</code>. Dữ liệu đọc từ <strong>hàng 25</strong> trở xuống.</li>
  <li>Bỏ dòng trống hoàn toàn, và bỏ dòng có <code>id = 0</code> (dấu hiệu "không xử lý").</li>
  <li><strong>Khung giờ của cả 5 ngày</strong> lấy từ cột <code>Shiptimeslot</code> của chính đơn hàng
    trong file Planning.
    <br /><strong class="ffc-danger-text">Không có khung giờ mặc định.</strong>
    Cột đó trống thì khung giờ của ngày đó <strong>trống</strong>, và ngày đó
    <strong>không khớp mức nào</strong> (cả 4 mức đều yêu cầu nhóm khung giờ).
    <br /><span class="ffc-muted st-small">Bảng Shiptimeslot chỉ dùng để tra <em>khung giờ → nhóm</em> (Sáng / Tối),
    không dùng làm giá trị mặc định và thứ tự các dòng trong đó không ảnh hưởng gì.</span>
  </li>
  <li><code>address_T{n}</code>: để trống → lấy <code>address_main</code>;
    có dấu <code>:</code> → tách thành "khung giờ : địa chỉ" (chỉ tách ở dấu <code>:</code> đầu tiên),
    và khung giờ ghi ở đây <strong>ghi đè</strong> khung giờ của riêng ngày đó;
    tiền tố <code>Giao tối</code> → khung giờ <code>1900/2200</code>.</li>
  <li><code>processed</code>: <code>yes</code> → dòng đó <strong>không được chia ship</strong>; mọi giá trị khác → <code>no</code>.</li>
  <li><code>nhom_shiptimeslot_T{n}</code>: tra khung giờ trong bảng Shiptimeslot — khớp chính xác trước,
    nếu không được thì khớp lại sau khi bỏ dấu và gộp khoảng trắng.</li>
  <li><code>nhom_packages</code>: tra mã package trong bảng Packages.
    <ul>
      <li>Để trống → <code>{blankGroup}</code> (setting <code>blank_nhom_packages</code>).</li>
      <li>Sai chính tả (<code>Fit3</code>, <code>MEAT - S</code>, <code>V EG</code>...) → vẫn khớp được nhờ so sánh sau khi bỏ dấu/khoảng trắng/gạch.</li>
      <li>Không tra được → để trống → <strong>dòng đó không khớp mức nào</strong> và bị ghi vào UnassignedReasons.</li>
    </ul>
  </li>
</ol>

<h2 class="st-h2">Bước 2 — Chia ship</h2>
<p>Mỗi ngày T2..T6 được xét <strong>độc lập</strong>. Dòng có <code>processed = yes</code> bị bỏ qua.</p>

<div class="ffc-alert ffc-alert-info st-callout">
  <strong>Nguyên tắc quan trọng nhất: đã khớp mức trên thì KHÔNG xét mức dưới.</strong>
  <br />
  Xét lần lượt Mức 1 → 2 → 3 → 4. Ngay khi một mức trả về <strong>Shipper khác rỗng</strong>,
  dừng lại và lấy kết quả đó; các mức còn lại <strong>không được chạy</strong>.
  Chỉ khi chưa có Shipper mới đi tiếp xuống mức dưới.
  <br /><br />
  Vì vậy Mức 1 <strong>luôn thắng</strong> Mức 2, Mức 2 luôn thắng Mức 3... kể cả khi mức dưới
  trông "đúng hơn". Muốn một rule thắng, phải đặt nó ở mức cao hơn — không phải sửa rule ở mức thấp.
  <br /><br />
  <strong>Ngoại lệ:</strong> nếu rule khớp đủ điều kiện nhưng ô <code>Shipper</code> để trống thì
  vẫn coi là <em>chưa có shipper</em> → tiếp tục xuống mức dưới, và ghi lý do vào UnassignedReasons.
</div>

<h3 class="st-h3">Mức 1 — Ship cố định (chi tiết)</h3>
<p>Duyệt <strong>toàn bộ</strong> bảng Ship cố định (Active). Một bản ghi được coi là ứng viên khi thoả <strong>cả 4</strong> điều kiện:</p>
<ol>
  <li><strong>Phone bằng nhau</strong> — hai bên đều bị <strong>xoá hết ký tự không phải chữ số</strong> rồi mới so.
    Nên <code>0912-345-678</code>, <code>0912345678</code>, <code>(0912) 345 678</code> là như nhau.
    So <strong>bằng tuyệt đối</strong>, không phải "chứa".</li>
  <li><strong>Nhóm khung giờ bằng nhau</strong> — lấy <code>Khung gio</code> của bản ghi,
    tra trong bảng Shiptimeslot để ra <code>Nhom khung gio</code>, rồi so với
    <code>nhom_shiptimeslot_T{n}</code> của ngày đang xét.
    <br /><span class="ffc-danger-text st-small">Nếu <code>Khung gio</code> của bản ghi không tra được (gõ sai, thừa ký tự lạ)
    thì nhóm = rỗng → bản ghi đó <strong>không bao giờ khớp</strong>.</span></li>
  <li><strong>Nhóm package bằng nhau</strong> — lấy <code>Packages</code> của bản ghi, tra bảng Packages ra nhóm
    (để trống → <code>{blankGroup}</code>), rồi so với <code>nhom_packages</code> của dòng đơn.
    <br /><span class="ffc-danger-text st-small">Mã package sai và không tra được → nhóm = rỗng → bản ghi <strong>không bao giờ khớp</strong>.</span></li>
  <li><strong>Điểm giống địa chỉ ≥ <code>{cdLevel}</code></strong> — so
    <code>address_T{n}</code> của ngày đang xét với <code>Dia chi</code> của bản ghi
    (<em>không</em> phải <code>address_main</code>).</li>
</ol>
<p>
  Trong các ứng viên, chọn bản ghi có <strong>điểm cao nhất</strong>; nếu bằng điểm thì lấy <strong>id nhỏ hơn</strong>.
  Ghi <code>rulematch = co dinh</code>, <code>notematch =</code> điểm %.
</p>
<p class="ffc-muted st-small">
  Cột <code>hascodinh_T{n}</code> chấm điểm theo <strong>phone thôi</strong> — bỏ qua cả 3 điều kiện còn lại và bỏ qua ngưỡng.
  Điểm cao mà không có <code>co dinh</code> ⇒ bị chặn bởi nhóm khung giờ hoặc nhóm package, không phải do địa chỉ.
</p>
<p class="ffc-danger-text st-small">
  Cảnh báo: nếu dòng đơn <strong>không có phone</strong> và bản ghi cũng <strong>không có phone</strong>,
  điều kiện 1 vẫn được coi là bằng nhau (rỗng = rỗng). Khi đó kết quả chỉ còn phụ thuộc địa chỉ — dễ khớp nhầm.
  Nên luôn điền phone cho bản ghi Ship cố định.
</p>

<h3 class="st-h3">Mức 2 — Wardshiprule <code>rule_order = 1</code> (chi tiết)</h3>
<p>Chỉ xét các rule có <code>rule_order = 1</code>. Một rule khớp khi thoả <strong>cả 4</strong>:</p>
<ol>
  <li><strong>Ward bằng nhau</strong> — <code>ward_T{n}</code> của dòng so với <code>Ward</code> của rule,
    sau khi chuẩn hoá. Là <strong>bằng tuyệt đối</strong>, không phải "chứa".
    <br /><span class="ffc-danger-text st-small">Nếu <code>ward_T{n}</code> trống thì Mức 2 và Mức 3 đều bị trượt —
    chỉ còn Mức 4 đỡ. Ward <strong>không</strong> được tự suy ra từ <code>address_main</code>.</span></li>
  <li><strong>Nhóm package bằng nhau</strong> — <code>nhom_packages</code> của dòng so với <code>Nhom Packages</code> của rule (chuẩn hoá).</li>
  <li><strong>Nhóm khung giờ bằng nhau</strong> — <code>nhom_shiptimeslot_T{n}</code> so với <code>Nhom Shiptimeslot</code> của rule (chuẩn hoá).</li>
  <li><strong>Street nằm trong địa chỉ</strong> — chuẩn hoá cả <code>Street</code> của rule và <code>address_T{n}</code>,
    rồi kiểm tra Street có phải là <strong>chuỗi con</strong> của địa chỉ hay không.
    <br />Đây là so <strong>chuỗi con thô</strong>, <strong>không theo biên từ</strong>: Street
    <code>"An Dương"</code> sẽ khớp cả địa chỉ <code>"An Dương Vương"</code>. Cần cẩn thận khi đặt tên Street ngắn.
    <br /><span class="ffc-muted st-small">Street để trống → rule bị bỏ qua (không tính là khớp).</span></li>
</ol>
<p>
  <strong>Thứ tự xét quyết định ai thắng</strong> khi nhiều rule cùng khớp — vì lấy rule khớp <strong>đầu tiên</strong>:
  <code>Priority</code> nhỏ chạy trước → <strong>để trống xếp sau tất cả các số</strong> → cùng mức thì theo tên Street (a→z).
  Ghi <code>rulematch = 1</code>, <code>notematch =</code> tên Street đã khớp.
</p>

<h3 class="st-h3">Mức 3 — Wardshiprule <code>rule_order = 5</code> (chi tiết)</h3>
<p>
  Đây là <strong>rule theo phường</strong>: cả phường dùng chung một shipper, không quan tâm đường nào.
  Chỉ chạy khi Mức 1 và Mức 2 đều không ra shipper. Một rule khớp khi thoả <strong>cả 3</strong>:
</p>
<ol>
  <li><strong>Ward bằng nhau</strong> — <code>ward_T{n}</code> so với <code>Ward</code> của rule, sau khi chuẩn hoá.
    Bằng tuyệt đối, không phải "chứa".
    <br /><span class="ffc-danger-text st-small"><code>ward_T{n}</code> trống → trượt luôn Mức 3, giống Mức 2.</span></li>
  <li><strong>Nhóm package bằng nhau</strong> — <code>nhom_packages</code> của dòng so với <code>Nhom Packages</code> của rule.</li>
  <li><strong>Nhóm khung giờ bằng nhau</strong> — <code>nhom_shiptimeslot_T{n}</code> so với <code>Nhom Shiptimeslot</code> của rule.</li>
</ol>
<p>
  Khác Mức 2 ở đúng một chỗ: <strong>không xét cột <code>Street</code></strong>. Nói cách khác, Mức 2 là
  "đường này giao cho ai", Mức 3 là "cả phường còn lại giao cho ai".
</p>
<p>
  <strong>Thứ tự xét:</strong> lấy rule khớp <strong>đầu tiên theo <code>id</code></strong> (thứ tự tạo / thứ tự
  dòng lúc import). <code>Priority</code> <strong>không</strong> áp dụng ở mức này.
  <br /><span class="ffc-danger-text st-small">Vì vậy nếu có 2 rule <code>rule_order = 5</code> trùng
  (ward + nhóm package + nhóm khung giờ), rule có <code>id</code> nhỏ hơn luôn thắng và rule kia
  <strong>không bao giờ chạy</strong> — dữ liệu thật có vài cặp trùng như vậy. Muốn đổi shipper thì sửa
  rule đang thắng, đừng thêm rule mới.</span>
  Ghi <code>rulematch = 5</code>, <code>notematch</code> để trống.
</p>

<h3 class="st-h3">Mức 4 — Wardshiprule <code>rule_order = 99</code> (chi tiết)</h3>
<p>
  Đây là <strong>lưới an toàn</strong>: chạy khi cả 3 mức trên đều không ra shipper, để đơn hàng vẫn có người giao.
  Một rule khớp khi thoả <strong>cả 2</strong>:
</p>
<ol>
  <li><strong>Nhóm package bằng nhau</strong> — so với <code>nhom_packages</code> của dòng.</li>
  <li><strong>Nhóm khung giờ bằng nhau</strong> — so với <code>nhom_shiptimeslot_T{n}</code> của ngày.</li>
</ol>
<p>
  <strong>Không xét ward, không xét street.</strong> Địa chỉ giao ở đâu cũng không ảnh hưởng — chỉ cần đúng
  nhóm package và nhóm khung giờ. Cột <code>Ward</code> của các rule này thường ghi
  <code>Còn lại</code> và <code>Street</code> ghi <code>ALL</code>, nhưng đó chỉ là <em>ghi chú cho người đọc</em>,
  code hoàn toàn không đọc hai cột đó ở mức này.
</p>
<p>
  <strong>Thứ tự xét:</strong> rule khớp <strong>đầu tiên theo <code>id</code></strong>, giống Mức 3.
  Thực tế mỗi cặp (nhóm package × nhóm khung giờ) chỉ cần <strong>một</strong> rule; các rule trùng phía sau
  không bao giờ chạy. Ghi <code>rulematch = 99</code>, <code>notematch</code> để trống.
</p>
<p class="ffc-danger-text">
  Cần đủ <strong>4 cặp</strong> để không bao giờ sót đơn:
  FITFOOD/Sáng, FITFOOD/Tối, CONO/Sáng, CONO/Tối.
  Thiếu cặp nào thì đơn thuộc cặp đó rơi hết xuống "không có shipper" và chỉ hiện trong sheet
  UnassignedReasons — đây là chỗ dễ sót nhất khi thêm nhóm package hoặc khung giờ mới.
</p>

<h3 class="st-h3">Bảng tóm tắt 4 mức</h3>
<table class="ffc-table st-doc-table">
  <thead>
    <tr><th>Mức</th><th>Nguồn</th><th>Điều kiện khớp</th><th>Chọn bản ghi nào</th><th><code>rulematch</code></th></tr>
  </thead>
  <tbody>
    <tr>
      <td><strong>1</strong></td><td>Ship cố định</td>
      <td>phone (chỉ lấy chữ số) + nhóm khung giờ + nhóm package
        + độ giống địa chỉ ≥ <code>{cdLevel}</code></td>
      <td>điểm giống <strong>cao nhất</strong>; bằng điểm thì id nhỏ hơn</td>
      <td><code>co dinh</code></td>
    </tr>
    <tr>
      <td><strong>2</strong></td><td>Wardshiprule <code>rule_order = 1</code></td>
      <td>ward + nhóm package + nhóm khung giờ + <code>Street</code> nằm trong địa chỉ</td>
      <td><code>Priority</code> nhỏ chạy trước; để trống xếp sau tất cả các số; cùng mức thì theo tên Street</td>
      <td><code>1</code></td>
    </tr>
    <tr>
      <td><strong>3</strong></td><td>Wardshiprule <code>rule_order = 5</code></td>
      <td>ward + nhóm package + nhóm khung giờ (không xét street)</td>
      <td>bản ghi đầu tiên theo id — <code>Priority</code> không áp dụng</td>
      <td><code>5</code></td>
    </tr>
    <tr>
      <td><strong>4</strong></td><td>Wardshiprule <code>rule_order = 99</code></td>
      <td>nhóm package + nhóm khung giờ (không xét ward, không xét street)</td>
      <td>bản ghi đầu tiên theo id — <code>Priority</code> không áp dụng</td>
      <td><code>99</code></td>
    </tr>
  </tbody>
</table>
<p>
  Điểm chung cả 4 mức: rule khớp đủ điều kiện nhưng <strong>ô Shipper để trống</strong> vẫn bị coi là
  không khớp — chạy tiếp xuống mức dưới và ghi lý do vào sheet UnassignedReasons.
</p>

<h2 class="st-h2">Kết quả — 47 cột, 3 sheet</h2>
<ul>
  <li><strong>Final</strong> — kết quả chia ship (sheet đầu tiên).</li>
  <li><strong>UnassignedReasons</strong> — từng ô ngày không có shipper kèm lý do.</li>
  <li><strong>B1</strong> — kết quả Bước 1, các cột chia ship còn trống (để đối chiếu).</li>
</ul>
<ul>
  <li><code>shipper_T{n}</code> — shipper được chia.</li>
  <li><code>rulematch_T{n}</code> — mức nào đã quyết định: <code>co dinh</code> / <code>1</code> / <code>5</code> / <code>99</code>.</li>
  <li><code>notematch_T{n}</code> — bằng chứng: mức 1 ghi điểm %, mức 2 ghi tên Street đã khớp.</li>
  <li><code>hascodinh_T{n}</code> — <strong>cột số 0–100</strong>: điểm giống địa chỉ với bất kỳ bản ghi Ship cố định
    <strong>trùng phone</strong>, kể cả khi mức 1 không chạy. Để trống = không có bản ghi nào trùng phone.
    Dùng để soi vì sao mức 1 không khớp: điểm cao mà không có <code>co dinh</code> nghĩa là bị chặn bởi
    nhóm khung giờ hoặc nhóm package; điểm 70–79 nghĩa là chỉ thiếu ngưỡng <code>ship_co_dinh_level</code>.</li>
</ul>

<h2 class="st-h2">Hướng dẫn Import Excel</h2>
<p>
  Cách sửa dữ liệu hàng loạt an toàn nhất: <strong>Export → sửa trong Excel → Import lại chính file đó</strong>.
  Cột <code>id</code> quyết định mỗi dòng là thêm mới hay cập nhật, nên
  <strong class="ffc-danger-text">đừng sửa hay xoá cột <code>id</code></strong> của dòng đã có.
</p>

<h3 class="st-h3">Các bước</h3>
<ol>
  <li>Vào đúng tab cần sửa, bấm <strong>⬇ Export</strong>. File tải về có cột <code>id</code> đứng đầu,
    <code>status</code> đứng cuối, và chứa <strong>cả Active lẫn Inactive</strong>.</li>
  <li>Sửa trong Excel. Muốn thêm dòng mới thì thêm ở cuối và <strong>để trống ô <code>id</code></strong>.</li>
  <li>Lưu lại dạng <code>.xlsx</code>, bấm <strong>⬆ Import</strong> và chọn file.</li>
  <li>Đọc bảng kết quả hiện ngay trên màn hình trước khi làm tiếp.</li>
</ol>

<h3 class="st-h3">Import xử lý từng dòng theo cột <code>id</code></h3>
<table class="ffc-table st-doc-table">
  <thead><tr><th>Ô <code>id</code></th><th>Kết quả</th><th>Hiện trong báo cáo</th></tr></thead>
  <tbody>
    <tr><td>để trống</td><td><strong>Thêm bản ghi mới</strong></td><td><span class="ffc-pill ffc-pill-ok">Created</span></td></tr>
    <tr><td>có, tồn tại, <strong>có thay đổi</strong></td><td>Cập nhật <strong>đúng những trường đã đổi</strong></td><td><span class="ffc-pill st-pill-info">Updated</span></td></tr>
    <tr><td>có, tồn tại, <strong>giống hệt</strong></td><td>Không ghi gì cả</td><td><span class="ffc-pill ffc-pill-warn">Skipped</span> + cảnh báo</td></tr>
    <tr><td>có nhưng <strong>không tồn tại</strong></td><td>Bỏ qua <em>riêng dòng đó</em>, các dòng khác vẫn chạy</td><td><span class="ffc-pill ffc-pill-danger">Failed</span> + lỗi</td></tr>
  </tbody>
</table>

<h3 class="st-h3">Những điểm hay nhầm</h3>
<ul>
  <li><strong>Import không xoá dữ liệu cũ.</strong> Dòng bị xoá khỏi file Excel vẫn còn nguyên trong hệ thống —
    muốn bỏ một bản ghi thì đặt <code>status = Inactive</code>, đừng xoá dòng.</li>
  <li>Cột <code>status</code>: <code>Active</code> / <code>Inactive</code>.
    Để trống khi cập nhật = <strong>giữ nguyên</strong> trạng thái đang có.</li>
  <li><strong>Tên cột không phân biệt hoa thường</strong>, bỏ qua khoảng trắng và gạch dưới, nên
    <code>packages</code> hay <code>Packages</code> đều nhận. Cột nào không có trong file thì trường đó
    <strong>giữ nguyên</strong>, không bị xoá.</li>
  <li>Chỉ đọc <strong>sheet đầu tiên</strong> của file.</li>
  <li><strong class="ffc-danger-text">Import file gốc <code>ShipManagement_Database.xlsx</code> hai lần sẽ nhân đôi dữ liệu</strong>,
    vì file đó không có cột <code>id</code> nên mọi dòng đều bị coi là thêm mới. Chỉ import file đó
    <strong>một lần</strong> lúc khởi tạo; sau đó luôn dùng file Export.</li>
  <li>Riêng tab <strong>Ship cố định</strong>: mã <code>Packages</code> không tra được sẽ hiện thẻ
    <span class="ffc-pill ffc-pill-warn">Package lạ</span>. Bản ghi vẫn được lưu nhưng
    <strong>không khớp đơn nào</strong> cho tới khi sửa mã.</li>
  <li>Riêng tab <strong>Setting</strong>: chỉ được sửa giá trị — không thêm mới, không deactivate,
    kể cả qua Import (dòng <code>id</code> trống hoặc <code>status = Inactive</code> bị bỏ qua kèm cảnh báo).</li>
</ul>

<h3 class="st-h3">Xoá dữ liệu</h3>
<p>
  Không có xoá thật ở bất kỳ đâu. Nút <strong>Ngưng dùng</strong> chỉ chuyển <code>status</code> sang
  <code>Inactive</code>; bản ghi vẫn nằm trong bảng và vẫn xuất ra khi Export, nhưng bị bỏ qua hoàn toàn
  khi chia ship. Bật ô <strong>Hiện mục ngưng dùng</strong> để xem lại.
</p>
`,
  },
}
